import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Passport, Plus } from "shared/assets/icons";
import { Button, Card, LoyaltyProgramTeaser, Loader, Message } from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingUserDocuments } from "Bands/utils";

const loyaltyCopy = copy.userLoyaltyProgram;

export const LoyaltyPrograms: FC = () => {
  const {
    loyaltyPrograms,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteModal,
    getItemActions,
  } = useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.loyaltyProgram,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.loyaltyProgram,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingLoyaltyPrograms } = useFetchingUserDocuments();

  const content = useMemo(() => {
    if (fetchingLoyaltyPrograms) {
      return <Loader />;
    }

    if (!loyaltyPrograms?.length) {
      return <Message message={loyaltyCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {loyaltyPrograms?.map(program => (
          <LoyaltyProgramTeaser key={program.id} {...program} actions={getActions(program.id)} />
        ))}
      </GridContainer>
    );
  }, [loyaltyPrograms, fetchingLoyaltyPrograms, getActions]);

  return (
    <Card icon={Passport} title={loyaltyCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.loyaltyProgram)}
          data-testid="add_loyalty_program_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{loyaltyCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
